if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.js-quantity-selector-input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.js-quantity-selector-wrapper');
        if (!this.quantityForm) return;
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.getElementById(`${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;

        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const newVariantSelects = html.querySelectorAll('variant-selects');
        const oldVariantSelects = this.querySelectorAll('variant-selects');
        if (newVariantSelects) {
          newVariantSelects.forEach((newVariantSelect, index) => {
            HTMLUpdateUtility.viewTransition(oldVariantSelects[index], newVariantSelects[index], this.preProcessHtmlCallbacks);
          });
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          const mediaId = variant?.featured_media?.id;
          const productId = variant?.featured_image?.product_id;
          if (mediaId && productId) {
            this.updateMedia(mediaId, productId);
          }
          this.updateSelectedVariant(variant?.id);
          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          if (!variant) {
            this.setUnavailable();
            return;
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('display--hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('Price');
          updateSourceFromDestination('SkuBarcode', ({ classList }) => classList.contains('display--hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('ProductQuickAdd', ({ classList }) => classList.contains('display--hidden'));

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateMedia(mediaId, productId) {
        if (!mediaId || !productId) return;
        const mediaGallery = document.getElementById(`MediaGallery-${this.sectionId}-${productId}`);
        const productCardMediaGallery = document.getElementById(`ProductCardMediaGallery-${this.sectionId}-${productId}`);

        if (mediaGallery) {
          mediaGallery.setActiveMedia(`${this.sectionId}-${productId}-${mediaId}`, true);
          mediaGallery.updateThumbnail(mediaId);
        } else if (productCardMediaGallery) {
          productCardMediaGallery.setActiveMedia(`${this.sectionId}-${productId}-${mediaId}`, true);
        }
      }

      updateSelectedVariant(variantId) {
        this.querySelectorAll('input[data-main-product-id]').forEach((input) =>{
          input.value = variantId;
        });
      }

      updateVariantInputs(variantId) {
        const productFormInstallments = document.getElementById(`product-form-installment-${this.dataset.section}`);
        let inputsToUpdate = [];
        if (productFormInstallments) {
          inputsToUpdate.push(...this.querySelectorAll(`input[data-main-product-id]`));
          inputsToUpdate.push(productFormInstallments.querySelector('input[name="id"]'));
        } else {
          inputsToUpdate.push(...this.querySelectorAll(`input[data-main-product-id]`));
        }
        inputsToUpdate.forEach((input) => {
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const updatedURL = `${url}${variantId ? `?variant=${variantId}` : ''}`;
        const socialShares = document.querySelectorAll('social-share');

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', updatedURL);
        socialShares.forEach((socialShare) => {
          socialShare.updateShareUrl(updatedURL);
        });
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'SkuBarcode', 'ProductQuickAdd']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('display--hidden'));
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
